import { api } from '../client';
import type { Timetable, UUID } from '@/types/domain';
import type { StudentResult } from '@/types/examination';
import type { Receipt } from '@/types/finance';
import type {
  ChildAssignments,
  ChildRef,
  MyAssignment,
  PortalAttendance,
  PortalFees,
  PortalLibrary,
  PortalStudent,
  PortalSubject,
  PortalTransport,
} from '@/types/portal';

/** `/portal/me` for a student, `/portal/children/{id}` for a guardian. */
function base(subject: PortalSubject): string {
  return subject === 'me' ? '/portal/me' : `/portal/children/${subject.studentId}`;
}

/** The cache key for a subject: 'me', or the child's studentId. */
export function subjectKey(subject: PortalSubject): string {
  return subject === 'me' ? 'me' : subject.studentId;
}

export const portalApi = {
  me: () => api.get<PortalStudent>('/portal/me'),
  children: () => api.get<ChildRef[]>('/portal/children'),

  /** Both dates optional: the server defaults to the last 30 days, and refuses more than 366. */
  attendance: (subject: PortalSubject, range: { from?: string; to?: string } = {}) =>
    api.get<PortalAttendance>(`${base(subject)}/attendance`, { params: range }),

  /** The published timetable in force today for the student's section; 404 when none. */
  timetable: (subject: PortalSubject) => api.get<Timetable>(`${base(subject)}/timetable`),

  /** Published results only, newest first. */
  results: (subject: PortalSubject) => api.get<StudentResult[]>(`${base(subject)}/results`),
  /** The full report card; 404 unless it exists and is published. */
  result: (subject: PortalSubject, examinationId: UUID) =>
    api.get<StudentResult>(`${base(subject)}/results/${examinationId}`),

  fees: (subject: PortalSubject) => api.get<PortalFees>(`${base(subject)}/fees`),
  /** 404 unless the payment belongs to this student. */
  receipt: (subject: PortalSubject, paymentId: UUID) =>
    api.get<Receipt>(`${base(subject)}/fees/payments/${paymentId}/receipt`),

  transport: (subject: PortalSubject) => api.get<PortalTransport>(`${base(subject)}/transport`),
  library: (subject: PortalSubject) => api.get<PortalLibrary>(`${base(subject)}/library`),
};

/** Coursework's own portal routes, which already existed before the portal API. */
export const courseworkPortalApi = {
  /** The signed-in student's set work, paged. */
  mine: (params: { page: number; size: number }) =>
    api.getPage<MyAssignment>('/assignments/mine', { params }),
  /** Every linked child's set work, grouped by child and paged per child. */
  children: (params: { page: number; size: number }) =>
    api.get<ChildAssignments[]>('/assignments/children', { params }),
};
